import { useEffect, useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { toast } from "sonner";
import axios from "axios";
import {
  RotateCcw, RotateCw, FlipHorizontal, FlipVertical, Crop as CropIcon,
  Loader2, RefreshCw,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const FULL_CROP = { unit: "%", x: 0, y: 0, width: 100, height: 100 };

/**
 * Full-featured image editor: rotate 90° in either direction, flip
 * horizontal/vertical, and an adjustable (drag-the-handles) crop box.
 * Rotate/flip commit immediately via `POST /api/admin/image-edit` — that
 * also bakes the transform server-side, so the crop box below always
 * lines up with pixels the way they'll actually be saved, and cross-
 * origin images (Rightmove CDN etc.) never need a client-side canvas
 * read, which CORS would block anyway.
 *
 * Props:
 *   open        — dialog open state
 *   onClose()   — close without saving
 *   sourceUrl   — the current image URL (local or absolute)
 *   onSaved(newUrl) — callback with the new /api/uploads/... URL
 *   aspect      — optional locked aspect ratio (e.g. 4/3); default: free
 */
export default function ImageEditorModal({ open, onClose, sourceUrl, onSaved, aspect }) {
  const [currentUrl, setCurrentUrl] = useState(sourceUrl);
  const [baking, setBaking] = useState(false);
  const [cropEnabled, setCropEnabled] = useState(false);
  const [crop, setCrop] = useState(FULL_CROP);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);

  // Every time the dialog opens (possibly for a different image), start
  // from a clean slate instead of whatever was left over from the last
  // image this same modal instance edited.
  useEffect(() => {
    if (open) {
      setCurrentUrl(sourceUrl);
      setCropEnabled(false);
      setCrop(FULL_CROP);
      setCompletedCrop(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceUrl]);

  const reset = () => {
    setCurrentUrl(sourceUrl);
    setCropEnabled(false);
    setCrop(FULL_CROP);
    setCompletedCrop(null);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const bakeTransform = async (payload) => {
    setBaking(true);
    try {
      const r = await axios.post(`${API}/admin/image-edit`, { url: currentUrl, ...payload }, { withCredentials: true });
      const newUrl = r.data?.url;
      if (!newUrl) throw new Error("Server did not return a URL");
      setCurrentUrl(newUrl);
      // Orientation just changed — a crop box drawn against the old
      // orientation no longer lines up, so start fresh.
      setCrop(FULL_CROP);
      setCompletedCrop(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Image edit failed");
    } finally {
      setBaking(false);
    }
  };

  const handleSave = async () => {
    const hasCrop = cropEnabled && completedCrop && completedCrop.width > 0 && completedCrop.height > 0
      && !(completedCrop.x === 0 && completedCrop.y === 0 && completedCrop.width >= 100 && completedCrop.height >= 100);

    if (!hasCrop && currentUrl === sourceUrl) {
      toast.message("No changes to save");
      return;
    }

    if (!hasCrop) {
      // Only rotate/flip were applied, and those are already baked into currentUrl.
      onSaved?.(currentUrl);
      reset();
      return;
    }

    setSaving(true);
    try {
      const img = imgRef.current;
      const scaleX = img.naturalWidth / 100;
      const scaleY = img.naturalHeight / 100;
      const payload = {
        url: currentUrl,
        crop: {
          x: Math.round(completedCrop.x * scaleX),
          y: Math.round(completedCrop.y * scaleY),
          width: Math.round(completedCrop.width * scaleX),
          height: Math.round(completedCrop.height * scaleY),
        },
      };
      const r = await axios.post(`${API}/admin/image-edit`, payload, { withCredentials: true });
      const newUrl = r.data?.url;
      if (!newUrl) throw new Error("Server did not return a URL");
      toast.success("Image updated");
      onSaved?.(newUrl);
      reset();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Image edit failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !baking && handleClose()}>
      <DialogContent
        className="max-w-4xl w-[95vw] max-h-[90vh] p-0 gap-0 rounded-none overflow-y-auto"
        data-testid="image-editor-modal"
      >
        <DialogHeader className="p-4 border-b border-border">
          <DialogTitle className="font-serif text-xl">Edit image</DialogTitle>
        </DialogHeader>

        {/* Crop canvas */}
        <div className="relative bg-neutral-900 flex items-center justify-center" style={{ minHeight: "50vh", maxHeight: "60vh" }}>
          {baking && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
          )}
          {currentUrl && (
            cropEnabled ? (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(_, percentCrop) => setCompletedCrop(percentCrop)}
                aspect={aspect}
                className="max-h-[60vh]"
              >
                <img
                  ref={imgRef}
                  src={currentUrl}
                  alt="Crop target"
                  style={{ maxHeight: "60vh", maxWidth: "100%", display: "block" }}
                  data-testid="img-edit-crop-target"
                />
              </ReactCrop>
            ) : (
              <img
                ref={imgRef}
                src={currentUrl}
                alt="Edit target"
                style={{ maxHeight: "60vh", maxWidth: "100%", display: "block" }}
              />
            )
          )}
          {!cropEnabled && !baking && (
            <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2">
              <div className="bg-black/50 text-white text-xs px-3 py-1 rounded-full">
                Turn on Crop, then drag the handles to adjust the selection
              </div>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-2 p-3 border-t border-border bg-neutral-50">
          <Button
            size="sm" variant="outline" disabled={baking} onClick={() => bakeTransform({ rotate: -90 })}
            data-testid="img-edit-rotate-ccw"
          >
            <RotateCcw size={14} className="mr-1" /> 90° left
          </Button>
          <Button
            size="sm" variant="outline" disabled={baking} onClick={() => bakeTransform({ rotate: 90 })}
            data-testid="img-edit-rotate-cw"
          >
            <RotateCw size={14} className="mr-1" /> 90° right
          </Button>
          <Button
            size="sm" variant="outline" disabled={baking} onClick={() => bakeTransform({ flip_horizontal: true })}
            data-testid="img-edit-flip-h"
          >
            <FlipHorizontal size={14} className="mr-1" /> Flip H
          </Button>
          <Button
            size="sm" variant="outline" disabled={baking} onClick={() => bakeTransform({ flip_vertical: true })}
            data-testid="img-edit-flip-v"
          >
            <FlipVertical size={14} className="mr-1" /> Flip V
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button
            size="sm" variant={cropEnabled ? "default" : "outline"} disabled={baking}
            onClick={() => setCropEnabled((v) => !v)}
            data-testid="img-edit-crop-toggle"
          >
            <CropIcon size={14} className="mr-1" /> {cropEnabled ? "Cropping" : "Crop"}
          </Button>
          <div className="flex items-center gap-1 ml-auto">
            <Button size="sm" variant="ghost" onClick={reset} title="Reset all" disabled={baking} data-testid="img-edit-reset">
              <RefreshCw size={14} />
            </Button>
          </div>
        </div>

        <DialogFooter className="p-3 border-t border-border sticky bottom-0 bg-background">
          <Button variant="outline" onClick={handleClose} disabled={saving || baking} data-testid="img-edit-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || baking} data-testid="img-edit-save">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
